import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send, AlertCircle, Upload, FileText, X, ChevronDown } from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import { SEO } from '../components/SEO';

export default function Contact() {
  const [status, setStatus] = useState('idle');
  const [searchParams] = useSearchParams();
  const subjectParam = searchParams.get('subject');
  const [presentations, setPresentations] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    fetchCollection('presentations').then(data => {
      const sortedData = [...data].sort((a, b) => {
        const titleA = a.title?.da || '';
        const titleB = b.title?.da || '';
        
        if (titleA === 'Relationskompetence') return -1;
        if (titleB === 'Relationskompetence') return 1;
        
        if (titleA === 'Hverdagens pædagogisk struktur') return -1;
        if (titleB === 'Hverdagens pædagogisk struktur') return 1;
        
        return 0;
      });
      setPresentations(sortedData);
    });
  }, []);

  useEffect(() => {
    if (subjectParam) {
      const decoded = decodeURIComponent(subjectParam);
      // Check if it matches a presentation title
      const hasMatch = presentations.some(p => p.title.da === decoded);
      if (hasMatch) {
        setSelectedSubject(`Oplæg: ${decoded}`);
      } else {
        setSelectedSubject(decoded);
      }
    } else {
      setSelectedSubject('');
    }
  }, [subjectParam, presentations]);

  const handleFileChange = (e) => {
    setFileError('');
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
    const maxFileSize = 10 * 1024 * 1024; // 10 MB
    const maxTotalSize = 20 * 1024 * 1024; // 20 MB

    let updatedFiles = [...selectedFiles];
    let hasError = false;

    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      
      // Check if it's a video to give a helpful specific message
      const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'];
      if (videoExtensions.includes(ext)) {
        setFileError('Videoer kan ikke uploades direkte her af pladshensyn. Del i stedet et link (f.eks. Google Drive, Dropbox, OneDrive) i din besked.');
        hasError = true;
        break;
      }

      if (!allowedExtensions.includes(ext)) {
        setFileError(`Filtypen .${ext} understøttes ikke. Tilladte formater: PNG, JPG, GIF, WEBP og PDF.`);
        hasError = true;
        break;
      }

      if (file.size > maxFileSize) {
        setFileError(`Filen "${file.name}" er for stor. Maksimal størrelse pr. fil er 10 MB.`);
        hasError = true;
        break;
      }

      // Check if file is already added
      if (updatedFiles.some(f => f.name === file.name && f.size === file.size)) {
        continue;
      }

      updatedFiles.push(file);
    }

    if (hasError) {
      e.target.value = ''; // Reset file input
      return;
    }

    // Check total count limit
    if (updatedFiles.length > 5) {
      setFileError('Du kan maksimalt vedhæfte 5 filer i alt.');
      e.target.value = '';
      return;
    }

    // Check total size limit
    const totalSize = updatedFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > maxTotalSize) {
      setFileError('Den samlede størrelse af filerne må ikke overstige 20 MB.');
      e.target.value = '';
      return;
    }

    setSelectedFiles(updatedFiles);
    e.target.value = ''; // Reset input so same file can be selected again if removed
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    
    const formElement = e.target;
    const formData = new FormData(formElement);

    // If selectedSubject is 'Marte Meo', we make sure the files from state are appended
    if (selectedSubject === 'Marte Meo') {
      // Remove any default files field to avoid duplicates
      formData.delete('files');
      // Append each file from our state
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
    } else {
      // If not Marte Meo, make sure no files are sent
      formData.delete('files');
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData, // No headers! Browser sets multipart boundary automatically
      });

      if (response.ok) {
        setStatus('success');
        formElement.reset();
        setSelectedFiles([]);
      } else {
        const errData = await response.json().catch(() => ({}));
        setErrorMsg(errData.message || 'Der skete en fejl under indsendelsen. Prøv venligst igen.');
        setStatus('error');
      }
    } catch (error) {
      setErrorMsg('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
      setStatus('error');
    }
  };

  return (
    <div className="pb-24">
      <SEO 
        title="Kontakt mig | Book et forløb eller pædagogisk oplæg"
        description="Brug for pædagogisk rådgivning, Theraplay, Marte Meo eller et oplæg? Kontakt mig i dag for en uforpligtende samtale om, hvordan jeg bedst hjælper jer."
        path="/kontakt"
      />
      {/* Hero Section */}
      <section className="relative h-[55vh] md:h-[65vh] min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src="https://assets.auxodev.dk/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/c3cb9746-8d8b-4d44-9436-78029021c2f3.png" 
          alt="Kontakt" 
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-stone-900/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 md:pt-32">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-light text-white uppercase tracking-tight"
          >
            Kontakt
          </motion.h1>
        </div>

        {/* Wave Divider at bottom of Hero */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
          <svg 
            viewBox="0 0 1200 120" 
            preserveAspectRatio="none" 
            className="relative block w-full h-12 md:h-24 fill-white"
          >
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:flex lg:flex-col lg:h-full"
          >
            <p className="text-xl text-stone-600 leading-relaxed mb-12">
              Har du spørgsmål til pædagogiske oplæg, Theraplay, Marte Meo eller ønsker du et uforpligtende tilbud? Du er altid velkommen til at række ud.
            </p>

            <div className="space-y-8 mb-12">
              <div>
                <h3 className="font-light text-stone-900 uppercase">Adresse</h3>
                <p className="text-stone-600">Højskolevej 1, 6000 Kolding</p>
              </div>
              <div>
                <h3 className="font-light text-stone-900 uppercase">Telefon</h3>
                <a href="tel:+4520487080" className="text-stone-600 hover:text-stone-800 transition-colors block">
                  +45 20 48 70 80
                </a>
              </div>
              <div>
                <h3 className="font-light text-stone-900 uppercase">Email</h3>
                <a href="mailto:vejentilbarnet@gmail.com" className="text-stone-600 hover:text-stone-800 transition-colors">
                  vejentilbarnet@gmail.com
                </a>
              </div>
            </div>

            <div className="mt-12 lg:mt-auto relative max-w-md mr-auto lg:mr-auto lg:ml-0 pb-6">
              {/* Green glow effect behind the image */}
              <div className="absolute -inset-8 bg-[#4e6354]/15 rounded-full blur-3xl opacity-60 pointer-events-none z-0" />
              
              {/* Decorative background element */}
              <div className="absolute -inset-3 bg-[#4e6354]/5 rounded-3xl rounded-tr-[80px] rounded-bl-[80px] transform -rotate-2 scale-105 pointer-events-none z-0" />
              
              {/* Image Container */}
              <div className="relative z-10">
                <div className="aspect-[4/5] rounded-3xl rounded-tr-[60px] rounded-bl-[60px] overflow-hidden shadow-lg border border-stone-200/40">
                  <img 
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/eff3456d-e9a8-4cc4-936d-27bf51e95b8f/8694174278989479663.jpg" 
                    alt="Familien og det pædagogiske ståsted" 
                    className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700 ease-out"
                  />
                </div>
                
                {/* Elegant floating card overlapping the image */}
                <div className="absolute -bottom-4 -right-2 left-6 bg-white/95 backdrop-blur-md p-5 rounded-2xl shadow-xl border border-stone-100/80 z-20 transform translate-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#4e6354]/10 flex items-center justify-center text-[#4e6354] shrink-0">
                      <i className="fa-solid fa-heart text-xs" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#4e6354] font-semibold mb-0.5">Mit pædagogiske ståsted</p>
                      <p className="text-sm text-stone-600 font-light leading-relaxed">
                        Tryghed, nærvær og faglig indsigt i øjenhøjde.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="flex flex-col gap-8 self-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-8 md:p-12 shadow-xl border border-stone-100"
            >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot */}
              <div className="sr-only absolute">
                <label htmlFor="website_url">Website URL</label>
                <input type="text" id="website_url" name="website_url" tabIndex="-1" autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">Navn</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-500 focus:border-stone-500 transition-all outline-none"
                    placeholder="Dit navn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-500 focus:border-stone-500 transition-all outline-none"
                    placeholder="din@email.dk"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-stone-700 mb-2">Emne</label>
                <div className="relative">
                  <select
                    id="subject"
                    name="subject"
                    value={selectedSubject}
                    required
                    onChange={(e) => {
                      setSelectedSubject(e.target.value);
                      if (e.target.value !== 'Marte Meo') {
                        setSelectedFiles([]);
                        setFileError('');
                      }
                    }}
                    className="w-full px-4 py-3.5 rounded-xl border border-stone-200 focus:ring-2 focus:ring-[#4e6354]/20 focus:border-[#4e6354] transition-all outline-none bg-white text-stone-800 font-light appearance-none cursor-pointer shadow-sm hover:border-stone-300"
                  >
                    <option value="">Vælg emne...</option>
                    <option value="Pædagogiske oplæg">Pædagogiske oplæg</option>
                    <option value="Theraplay">Theraplay</option>
                    <option value="Marte Meo">Marte Meo</option>
                    {presentations.length > 0 && (
                      <optgroup label="Pædagogiske oplæg (Emner)" className="font-normal text-stone-500 bg-stone-50">
                        {presentations.map((item) => (
                          <option key={item.id} value={`Oplæg: ${item.title.da}`} className="text-stone-800 bg-white">
                            Oplæg: {item.title.da}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <option value="Andet">Andet</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-[#4e6354]">
                    <ChevronDown className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {selectedSubject === 'Marte Meo' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-stone-700">
                    Vedhæft billeder eller dokumenter <span className="text-stone-400 font-normal">(Valgfrit)</span>
                  </label>
                  <div className="border-2 border-dashed border-stone-200 rounded-xl p-6 text-center hover:border-[#4e6354] transition-colors relative cursor-pointer group">
                    <input
                      type="file"
                      id="files"
                      name="files"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <div className="p-3 bg-stone-50 rounded-full text-stone-500 group-hover:bg-[#4e6354]/10 group-hover:text-[#4e6354] transition-colors">
                        <Upload className="w-6 h-6" />
                      </div>
                      <p className="text-sm text-stone-600">
                        <span className="font-semibold text-[#4e6354]">Klik for at vælge filer</span> eller træk dem hertil
                      </p>
                      <p className="text-xs text-stone-400 max-w-md mx-auto leading-relaxed">
                        Understøtter: PNG, JPG, GIF, WEBP og PDF (Maks. 5 filer, max 10 MB pr. fil, i alt 20 MB).
                        <br />
                        <span className="text-stone-500 font-medium">Videoer:</span> Del i stedet et link (f.eks. Google Drive, Dropbox) i din besked.
                      </p>
                    </div>
                  </div>

                  {/* File validation error */}
                  {fileError && (
                    <p className="text-red-500 text-xs flex items-center gap-1.5 font-medium">
                      <AlertCircle className="w-4 h-4" /> {fileError}
                    </p>
                  )}

                  {/* Selected files list */}
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Valgte filer ({selectedFiles.length}/5):</p>
                      <div className="divide-y divide-stone-100 border border-stone-200/60 rounded-xl overflow-hidden bg-stone-50">
                        {selectedFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 text-sm">
                            <div className="flex items-center gap-2 truncate pr-4">
                              <FileText className="w-4 h-4 text-stone-400 shrink-0" />
                              <span className="truncate text-stone-700 font-medium">{file.name}</span>
                              <span className="text-xs text-stone-400 shrink-0">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveFile(idx)}
                              className="text-stone-400 hover:text-red-500 transition-colors p-1"
                              title="Fjern fil"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">Besked</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                  className="w-full px-4 py-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-stone-500 focus:border-stone-500 transition-all outline-none resize-none"
                  placeholder="Hvordan kan jeg hjælpe dig?"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-stone-900 text-white py-4 rounded-xl font-bold hover:bg-stone-800 transition-all hover:-translate-y-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {status === 'submitting' ? 'Sender...' : (
                  <>
                    Send besked <Send className="w-5 h-5" />
                  </>
                )}
              </button>

              {status === 'success' && (
                <p className="text-green-600 text-center font-medium">Tak for din besked! Jeg vender tilbage hurtigst muligt.</p>
              )}
              {status === 'error' && (
                <p className="text-red-500 text-center font-medium">{errorMsg || 'Der skete en fejl. Prøv venligst igen senere.'}</p>
              )}
            </form>
          </motion.div>

          <div className="bg-white p-8 rounded-3xl shadow-lg border border-stone-100 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-stone-800 shrink-0 mt-1" />
            <div>
              <h3 className="font-light text-stone-900 mb-2 uppercase">Vigtig information</h3>
              <p className="text-stone-800">
                Ingen kunder fra Kolding Kommune, da jeg stadig er ansat i Kolding Kommune.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
